import { Button } from "@/modern/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/modern/components/ui/dropdown-menu";
import { MoreVertical } from "lucide-react";
import { useEntityChecklistContext } from "../context/EntityChecklistContext";
import { useRef, useState } from "react";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { getIsArchivedForBoard } from "@/other/utils/otherUtils";
import { NudgeUsersModal } from "./editors/NudgeUsersModal";
import { ChecklistMenuItems } from "./ChecklistMenuItems";

export const ChecklistMenu = () => {
  const {
    selectedChecklist,
    toggleArchiveChecklist,
    copyChecklistLink,
    duplicateChecklistRecord,
    importChecklistItems,
    exportChecklistItems,
    deleteChecklist,
    handleHideChecklistDetails,
    handleCloseEditor,
    updateChecklist,
    newChecklist,
    isCreatingNewChecklist,
    canEditSelectedChecklist
  } = useEntityChecklistContext();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isNudgeModalOpen, setIsNudgeModalOpen] = useState(false);
  const canEdit = canEditSelectedChecklist;

  const checklist = newChecklist || selectedChecklist;
  if (!checklist) return null;

  const handleArchive = () => {
    if (!checklist.archived) {
      const selectedBoard = getSavedBoardSelection();
      const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);
      if (!canShowArchivedRecords) {
        handleHideChecklistDetails();
        handleCloseEditor();
      }
    }
    toggleArchiveChecklist(checklist);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      void importChecklistItems(checklist, file);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async () => {
    // Close editor UI first; delete mutation also drops the row from list cache
    // so any still-mounted tile provider disables its detail query (avoids 404).
    handleHideChecklistDetails();
    handleCloseEditor();
    await deleteChecklist(checklist);
  };

  return (
    <>
      <input ref={fileInputRef} type="file" accept=".txt" style={{ display: "none" }} onChange={handleFileChange} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" disabled={!!newChecklist || isCreatingNewChecklist}>
            <MoreVertical className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <ChecklistMenuItems
            archived={checklist.archived}
            canEdit={canEdit}
            onArchive={handleArchive}
            onCopyLink={() => copyChecklistLink(checklist)}
            onDuplicate={() => duplicateChecklistRecord(checklist)}
            onImport={() => fileInputRef.current?.click()}
            onExport={() => void exportChecklistItems(checklist)}
            onNudge={() => setIsNudgeModalOpen(true)}
            onDelete={() => void handleDelete()}
          />
        </DropdownMenuContent>
      </DropdownMenu>

      <NudgeUsersModal
        open={isNudgeModalOpen}
        onOpenChange={setIsNudgeModalOpen}
        checklist={checklist}
        onUpdateChecklist={updateChecklist}
      />
    </>
  );
};
